// Finds a class's exam attempts exam by exam: OnlineExam_Submission has no email index, but `exam` is
// indexed, so the class's course units name the exams and each one is read on its own. Import-free, for node --test.

import type { Id, IztContentNode, IztExamSubmission } from '../../api/types';
import { contentKind } from '../../config/content-types.ts';
import { STUDENT_EMAIL_DOMAIN_UNSET } from '../submissions/submissions.ts';
import { examTitlesFromUnits } from './examResults.ts';

export const EXAM_CONCURRENCY = 6;

export const IZ_SIGNED_OUT = 'Cần đăng nhập IZTeach để xem bài kiểm tra của lớp. Hãy đăng nhập tài khoản giáo viên IZTeach trong trang thiết lập.';

/** Why a load stops before any request: an error to show, or `{ error: null }` for an empty roster; null to go on. */
export function examLoadBlock(domain: string, pattern: string, hasToken: boolean): { error: string | null } | null {
  if (!domain) return { error: STUDENT_EMAIL_DOMAIN_UNSET };
  if (!pattern) return { error: null };
  // unit reads need the IZ token, and without them there is no fast way to name the class's exams
  if (!hasToken) return { error: IZ_SIGNED_OUT };
  return null;
}

/** One class's exam load; a token arriving after mount changes it, so the load runs again. */
export function examLoadKey(input: { codes: string[]; classId: Id; gradeId?: Id; subjectId?: Id }, signedIn: boolean): string {
  return [input.classId, input.gradeId || '', input.subjectId || '', signedIn ? 'iz' : '', input.codes.join(',')].join('|');
}

/** Kết quả on a test unit's rail: closes the card when it shows this exam, else opens it. */
export function examResultsAction(openExamId: string | null, examId: string | null): 'open' | 'close' | null {
  if (!examId) return null;
  return openExamId === examId ? 'close' : 'open';
}

export interface ExamUnit {
  id?: string;
  _id?: string;
  title?: string | null;
  type?: string | null;
  exam?: string | null;
}

export interface ClassExamDeps {
  /** The course's units with title, type and exam (a token-gated read). */
  unitsOf: (courseId: string) => Promise<ExamUnit[]>;
  /** Attempts at one exam, already narrowed to the roster pattern where the service can. */
  attemptsOf: (examId: string) => Promise<IztExamSubmission[]>;
}

export interface ClassExamRows {
  rows: IztExamSubmission[];
  examIds: string[];
  /** exam id to its unit's title. */
  examTitles: Record<string, string>;
  /** IZ course id to its name, from the tree. */
  courseTitles: Record<string, string>;
  /** exam id to the test unit wrapping it, so the card can open that unit on the board. */
  examUnits: Record<string, ExamUnitRef>;
}

export interface ExamUnitRef {
  unitId: string;
  courseId: string;
  title: string;
}

/** First test unit per exam, by course. */
export function examUnitsFrom(courseIds: string[], unitLists: ExamUnit[][]): Record<string, ExamUnitRef> {
  const out: Record<string, ExamUnitRef> = {};
  unitLists.forEach((units, i) => units.forEach((unit) => {
    const exam = unit.exam ? String(unit.exam) : '';
    const unitId = String(unit.id || unit._id || '');
    if (!exam || !unitId || out[exam] || contentKind(unit.type || null) !== 'assessment') return;
    out[exam] = { unitId, courseId: courseIds[i], title: String(unit.title || '').trim() };
  }));
  return out;
}

/** Tree courses holding at least one test unit; the others cannot have an exam to read. */
export function assessmentCourses(tree: IztContentNode[]): IztContentNode[] {
  const seen: Record<string, boolean> = {};
  return tree.filter((course) => {
    if (!course.id || seen[course.id]) return false;
    seen[course.id] = true;
    return course.sections.some((section) => section.units.some((unit) => contentKind(unit.type) === 'assessment'));
  });
}

/** Exam ids of the test units, first seen first, without repeats. */
export function examIdsFromUnits(units: ExamUnit[]): string[] {
  const ids: string[] = [];
  units.forEach((unit) => {
    const exam = unit.exam ? String(unit.exam) : '';
    if (exam && contentKind(unit.type || null) === 'assessment' && ids.indexOf(exam) < 0) ids.push(exam);
  });
  return ids;
}

/** Rows whose email matches the anchored roster pattern, case-insensitive like the service. */
export function rosterRows(rows: IztExamSubmission[], pattern: string): IztExamSubmission[] {
  if (!pattern) return [];
  const matcher = new RegExp(pattern, 'i');
  return rows.filter((row) => matcher.test(String(row.email || '').trim()));
}

/** `run` over `items` with at most `limit` in flight; results keep the input order, the first failure rejects. */
export async function mapLimit<T, R>(items: T[], limit: number, run: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await run(items[index]);
    }
  }
  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(Math.max(1, limit), items.length); i += 1) workers.push(worker());
  await Promise.all(workers);
  return results;
}

/** Course units, then one read per exam; only rows matching the roster come back. */
export async function loadClassExamRows(deps: ClassExamDeps, tree: IztContentNode[], pattern: string, concurrency: number = EXAM_CONCURRENCY): Promise<ClassExamRows> {
  const courses = assessmentCourses(tree);
  const courseTitles: Record<string, string> = {};
  courses.forEach((course) => { if (course.name) courseTitles[course.id] = course.name; });
  const unitLists = await mapLimit(courses, concurrency, (course) => deps.unitsOf(course.id));
  const units = unitLists.reduce<ExamUnit[]>((all, list) => all.concat(list), []);
  const examIds = examIdsFromUnits(units);
  const examTitles = examTitlesFromUnits(units.filter((unit) => contentKind(unit.type || null) === 'assessment'));
  const perExam = await mapLimit(examIds, concurrency, (examId) => deps.attemptsOf(examId));
  const rows = rosterRows(perExam.reduce<IztExamSubmission[]>((all, list) => all.concat(list), []), pattern);
  const examUnits = examUnitsFrom(courses.map((course) => course.id), unitLists);
  return { rows, examIds, examTitles, courseTitles, examUnits };
}
